import React from 'react';

import {
  fetchLandingPages,
  fetchLivePages,
  landingHeroUrl,
  landingPosterVersion,
  type LandingPage,
} from '../landing-page-api';
import { landingDestinationLabel, sameDestination } from '../tracking-link';

/** One place a tracking link can send traffic to instead of the casino: a landing page of the brand. */
export interface LandingDestination {
  /** `Welcome wheel · lp.brand.com/welcome-wheel` (data: the page's own name and address). */
  label: string;
  /** The page's own name ('' when it has none). Data. */
  name: string;
  /** A picture of the page for a list: the Studio page's key art (the owner's image, else the generated hero, else
   *  its card image). Absent for a page Studio did not make, or one with no art yet. */
  imageUrl?: string;
  /** The page's absolute URL (`https://lp.brand.com/welcome-wheel`); a link's `aff_id` is appended to it. */
  url: string;
  /** `live`: a page the brand's landing host serves now (the live list). `draft`: a Studio landing page published
   *  live that the live list does not show yet. */
  source: 'live' | 'draft';
  /** The Studio landing page's spark id, when Studio made the page (matched by its public id or address): a link is
   *  pinned to it by this id (`landing_page_id`), so a republish at a new address keeps the link working. Absent for
   *  a live page Studio did not make (stored by its URL alone). */
  landingPageId?: string;
}

export interface UseLandingDestinations {
  destinations: LandingDestination[];
  /** The brand's landing hosts (`lp.brand.com`, lowercase): the live list's hosts and the hosts the Studio landing pages
   *  were published on (live or not), to tell a link to a landing page no longer listed (unpublished) from the casino. */
  hosts: string[];
  /** True until the first read for this brand lands. */
  loading: boolean;
  /** The read failed (the list is then empty, so the sheet says the brand has no landing page). */
  error: string | null;
  /** Only one of the two lists was read (the other failed): the list can miss pages, and a page's Studio id may be
   *  unknown, so a page not found in it is unknown rather than absent. Not kept, so the next read asks again. */
  partial: boolean;
  /** Read the brand's pages again now (dropping the shared copy), e.g. once a page made from a link panel is live. */
  reload: () => void;
}

type DestinationsRead = Omit<UseLandingDestinations, 'loading' | 'reload'>;

/** Read once per brand and kept briefly (the live list is asked for without its pages' HTML): every tracking link of
 *  a composer (one per version) shares it. */
const CACHE_MS = 2 * 60 * 1000;

interface CacheEntry {
  at: number;
  request: Promise<DestinationsRead>;
}

/** A URL's host, lowercase ('' when it is not an absolute URL). Pure. */
const hostOf = (url: string): string => {
  try {
    return new URL(url.trim()).hostname.toLowerCase();
  } catch {
    return '';
  }
};

/** Whether a URL lives on one of the brand's landing hosts. Pure. */
export const onLandingHost = (url: string | undefined, hosts: readonly string[]): boolean => {
  const host = url ? hostOf(url) : '';
  return host !== '' && hosts.includes(host);
};

const cache = new Map<number, CacheEntry>();

const readDestinations = (tenantId: number): CacheEntry['request'] => {
  const hit = cache.get(tenantId);
  if (hit && Date.now() - hit.at < CACHE_MS) return hit.request;
  const request = Promise.all([
    fetchLivePages(tenantId, undefined, { includeHtml: false }),
    fetchLandingPages(tenantId),
  ]).then(([live, studio]) => {
    const destinations: LandingDestination[] = [];
    const hosts = new Set<string>();
    const addHost = (host: string): void => {
      const h = host.trim().toLowerCase();
      if (h) hosts.add(h);
    };
    // The Studio pages that went live at some point (the live ones first), to pin a live-list page to its Studio id
    // (by public id, else address).
    const studioPages = studio.ok
      ? studio.data
          .filter((page) => !!page.publication)
          .sort((a, b) => Number(b.publication?.state === 'live') - Number(a.publication?.state === 'live'))
      : [];
    const studioPageFor = (lpid: string, url: string): LandingPage | undefined =>
      studioPages.find((page) => {
        const pub = page.publication;
        return !!pub && ((!!lpid && pub.lpid === lpid) || sameDestination(pub.url, url));
      });
    const add = (
      name: string,
      url: string,
      source: LandingDestination['source'],
      page: LandingPage | undefined,
    ): void => {
      // A page with no address (the brand has no landing host yet) cannot be linked to.
      if (!url.trim() || destinations.some((d) => sameDestination(d.url, url))) return;
      const imageUrl = page ? landingHeroUrl(landingPosterVersion(page)) || page.image_url : '';
      destinations.push({
        label: landingDestinationLabel(name, url),
        name: name.trim(),
        url: url.trim(),
        source,
        ...(page ? { landingPageId: page.id } : {}),
        ...(imageUrl ? { imageUrl } : {}),
      });
    };
    if (live.ok) {
      live.data.hosts.forEach((h) => addHost(h.lp_domain));
      live.data.pages.forEach((page) =>
        add(page.name || page.title || page.path, page.url, 'live', studioPageFor(page.lpid, page.url)),
      );
    }
    // Studio pages published live that the live list does not show (yet), e.g. one published moments ago.
    if (studio.ok) {
      studio.data.forEach((page) => {
        const pub = page.publication;
        if (pub) {
          addHost(pub.lp_domain);
          addHost(hostOf(pub.url));
        }
        if (!page.archived && pub?.state === 'live' && pub.url) add(page.name, pub.url, 'draft', page);
      });
    }
    if (live.ok && studio.ok) return { destinations, hosts: [...hosts], error: null, partial: false };
    // One read or neither: not kept, so the next tracking link asks again.
    cache.delete(tenantId);
    if (live.ok || studio.ok) return { destinations, hosts: [...hosts], error: null, partial: true };
    return {
      destinations,
      hosts: [],
      error: live.error || `landing pages unavailable (${live.status})`,
      partial: false,
    };
  });
  cache.set(tenantId, { at: Date.now(), request });
  return request;
};

/** Forget a brand's cached destinations (e.g. right after a landing page is published or unpublished), so the next
 *  sheet reads them again. */
export const invalidateLandingDestinations = (tenantId: number): void => {
  cache.delete(tenantId);
};

/**
 * The brand's landing pages a tracking link can point at, for the link destination sheet. Read from the live list (`GET /creatives/tenant/{t}/landing-pages/live?include_html=false`) plus the Studio landing pages
 * published live that it does not show yet (`GET /creatives/tenant/{t}/landing-pages`), shared per brand for a couple of
 * minutes. No tenant: nothing, not loading.
 */
export const useLandingDestinations = (tenantId: number | undefined): UseLandingDestinations => {
  const [state, setState] = React.useState<Omit<UseLandingDestinations, 'reload'>>(() => ({
    destinations: [],
    hosts: [],
    loading: !!tenantId,
    error: null,
    partial: false,
  }));
  const [tick, setTick] = React.useState(0);
  // Syncs with the network: reads the brand's live landing pages (cached per tenant).
  React.useEffect(() => {
    if (!tenantId) {
      setState({ destinations: [], hosts: [], loading: false, error: null, partial: false });
      return undefined;
    }
    let live = true;
    // A reload keeps the current list on screen until the new one lands.
    setState((cur) => (cur.loading || tick > 0 ? cur : { ...cur, loading: true }));
    void readDestinations(tenantId).then(({ destinations, hosts, error, partial }) => {
      if (live) setState({ destinations, hosts, loading: false, error, partial });
    });
    return () => {
      live = false;
    };
  }, [tenantId, tick]);
  const reload = React.useCallback((): void => {
    if (tenantId) invalidateLandingDestinations(tenantId);
    setTick((n) => n + 1);
  }, [tenantId]);
  return { ...state, reload };
};
